/**
 * Kitchen-readable measures. A quantity is stored once, in the ingredient's base unit, and shown
 * in whatever the person at the counter measures with:
 *
 * - 'metric'     g / kg, ml / l, each
 * - 'us_weight'  oz, then "1 lb 5.3 oz" from 16 oz; fl oz for a liquid with no density
 * - 'us_volume'  cups and spoons with kitchen fractions ("2 tbsp + 1 ½ tsp", "1 ⅓ cups");
 *                falls back to us_weight when there is no density to measure by volume
 * - 'imperial'   oz / lb / st for mass; imp fl oz and imp pt for volume
 * - 'entered'    the line exactly as it was entered ("1 × 28 oz can", "5 cups")
 *
 * Rounding follows the production sheets: grams under 10 to one decimal, otherwise whole grams;
 * ounces under 10 to two decimals, otherwise one.
 */

import { getUnit } from './catalog.ts';
import {
  densityOf,
  eachWeightOf,
  type ConvertibleIngredient,
  type NumericInput,
} from './conversions.ts';

export type MeasureStyle = 'metric' | 'us_weight' | 'us_volume' | 'imperial' | 'entered';

/** A recipe or receipt line as the owner entered it (recipe_version_ingredients.entry_*). */
export interface EnteredMeasure {
  quantity: NumericInput | null;
  unit?: string | null;
  package_id?: string | null;
  label?: string | null;
}

const G_PER_OZ = 28.349523125;
const OZ_PER_LB = 16;
const LB_PER_ST = 14;
const ML_PER_TSP = 4.92892159375;
const TSP_PER_TBSP = 3;
const TSP_PER_CUP = 48;
const ML_PER_FLOZ = 29.5735295625;
const ML_PER_IMP_FLOZ = 28.4130625;
const ML_PER_IMP_PT = 568.26125;

const numberFormats = new Map<number, Intl.NumberFormat>();
/** en-US grouping, up to `decimals` fraction digits, trailing zeros dropped. */
export function formatNumber(value: number, decimals = 2): string {
  let f = numberFormats.get(decimals);
  if (!f) {
    f = new Intl.NumberFormat('en-US', {
      maximumFractionDigits: decimals,
      minimumFractionDigits: 0,
    });
    numberFormats.set(decimals, f);
  }
  const out = f.format(value);
  return out === '-0' ? '0' : out;
}

function roundTo(value: number, decimals: number): number {
  const p = 10 ** decimals;
  // Pre-round away binary noise so 1.005 rounds like the decimal it was written as.
  return Math.round(Number((value * p).toFixed(6))) / p;
}

/** The sheets' gram rule: one decimal under 10, whole numbers above. */
function roundSheet(value: number): number {
  return value < 10 ? roundTo(value, 1) : Math.round(value);
}

function formatMetric(value: number, small: string, large: string): string {
  if (value >= 10000) return `${formatNumber(roundTo(value / 1000, 2), 2)} ${large}`;
  return `${formatNumber(roundSheet(value), 1)} ${small}`;
}

function formatCount(value: number): string {
  return `${formatNumber(roundTo(value, 2), 2)} each`;
}

/** The sheets' ounce rule: two decimals under 10, one above. */
function roundOunces(oz: number): number {
  return oz < 10 ? roundTo(oz, 2) : roundTo(oz, 1);
}

function formatOunces(grams: number): string {
  const oz = grams / G_PER_OZ;
  const rounded = roundOunces(oz);
  if (rounded < OZ_PER_LB) return `${formatNumber(rounded, 2)} oz`;
  return formatPoundsOunces(oz);
}

function formatPoundsOunces(oz: number): string {
  let lb = Math.floor(oz / OZ_PER_LB);
  let rem = roundTo(oz - lb * OZ_PER_LB, 1);
  if (rem >= OZ_PER_LB) {
    lb += 1;
    rem = 0;
  }
  return rem > 0
    ? `${formatNumber(lb, 0)} lb ${formatNumber(rem, 1)} oz`
    : `${formatNumber(lb, 0)} lb`;
}

// Eighths and thirds, the fractions the production sheets print.
const FRACTIONS: ReadonlyArray<readonly [number, string]> = [
  [0, ''],
  [1 / 8, '⅛'],
  [1 / 4, '¼'],
  [1 / 3, '⅓'],
  [3 / 8, '⅜'],
  [1 / 2, '½'],
  [5 / 8, '⅝'],
  [2 / 3, '⅔'],
  [3 / 4, '¾'],
  [7 / 8, '⅞'],
  [1, ''],
];

/** Snap to the nearest whole plus kitchen fraction (⅛ ¼ ⅓ ⅜ ½ ⅝ ⅔ ¾ ⅞). */
function snapKitchen(value: number): { whole: number; glyph: string; value: number } {
  let whole = Math.floor(value);
  const frac = value - whole;
  let best = FRACTIONS[0]!;
  for (const f of FRACTIONS) if (Math.abs(frac - f[0]) < Math.abs(frac - best[0])) best = f;
  if (best[0] === 1) {
    whole += 1;
    return { whole, glyph: '', value: whole };
  }
  return { whole, glyph: best[1], value: whole + best[0] };
}

function kitchenNumber(s: { whole: number; glyph: string }): string {
  if (s.whole === 0) return s.glyph || '0';
  return s.glyph ? `${formatNumber(s.whole, 0)} ${s.glyph}` : formatNumber(s.whole, 0);
}

/** Millilitres as cups, or tablespoons plus teaspoons below a quarter cup. */
export function formatKitchenVolume(ml: number): string {
  if (!(ml > 0)) return '0 tsp';
  const tsp = ml / ML_PER_TSP;
  if (tsp < TSP_PER_CUP / 4) {
    let tbsp = Math.floor(tsp / TSP_PER_TBSP);
    let rem = snapKitchen(tsp - tbsp * TSP_PER_TBSP);
    if (rem.value >= TSP_PER_TBSP) {
      tbsp += 1;
      rem = { whole: 0, glyph: '', value: 0 };
    }
    if (tbsp * TSP_PER_TBSP < TSP_PER_CUP / 4) {
      if (tbsp === 0) return rem.value === 0 ? 'a pinch' : `${kitchenNumber(rem)} tsp`;
      return rem.value === 0 ? `${tbsp} tbsp` : `${tbsp} tbsp + ${kitchenNumber(rem)} tsp`;
    }
  }
  const cups = snapKitchen(tsp / TSP_PER_CUP);
  return `${kitchenNumber(cups)} ${cups.value > 1 ? 'cups' : 'cup'}`;
}

function gramsOf(baseQty: number, ingredient: ConvertibleIngredient): number | null {
  if (ingredient.base_unit === 'g') return baseQty;
  if (ingredient.base_unit === 'ml') {
    const d = densityOf(ingredient);
    return d === null ? null : baseQty * d;
  }
  const each = eachWeightOf(ingredient);
  return each === null ? null : baseQty * each;
}

function millilitresOf(baseQty: number, ingredient: ConvertibleIngredient): number | null {
  if (ingredient.base_unit === 'ml') return baseQty;
  const d = densityOf(ingredient);
  if (d === null) return null;
  const grams = gramsOf(baseQty, ingredient);
  return grams === null ? null : grams / d;
}

function formatUsWeight(baseQty: number, ingredient: ConvertibleIngredient): string {
  const grams = gramsOf(baseQty, ingredient);
  if (grams !== null) return formatOunces(grams);
  if (ingredient.base_unit === 'ml') {
    return `${formatNumber(roundOunces(baseQty / ML_PER_FLOZ), 2)} fl oz`;
  }
  return formatCount(baseQty);
}

function formatImperialMass(grams: number): string {
  const oz = grams / G_PER_OZ;
  if (roundOunces(oz) < OZ_PER_LB) return `${formatNumber(roundOunces(oz), 2)} oz`;
  const lbTotal = oz / OZ_PER_LB;
  if (roundTo(lbTotal, 1) < LB_PER_ST) return formatPoundsOunces(oz);
  let st = Math.floor(lbTotal / LB_PER_ST);
  let lb = roundTo(lbTotal - st * LB_PER_ST, 1);
  if (lb >= LB_PER_ST) {
    st += 1;
    lb = 0;
  }
  return lb > 0
    ? `${formatNumber(st, 0)} st ${formatNumber(lb, 1)} lb`
    : `${formatNumber(st, 0)} st`;
}

function formatImperialVolume(ml: number): string {
  const flOz = roundOunces(ml / ML_PER_IMP_FLOZ);
  if (flOz < 20) return `${formatNumber(flOz, 2)} imp fl oz`;
  return `${formatNumber(roundTo(ml / ML_PER_IMP_PT, 2), 2)} imp pt`;
}

function formatEntered(entered: EnteredMeasure): string | null {
  if (entered.quantity === null || entered.quantity === undefined || entered.quantity === '')
    return null;
  const qty = Number(entered.quantity);
  if (!Number.isFinite(qty)) return null;
  const n = formatNumber(roundTo(qty, 3), 3);
  const unitAbbr = entered.unit ? getUnit(entered.unit)?.abbr : undefined;
  const label = entered.label?.trim() || unitAbbr;
  if (!label) return null;
  if (entered.package_id) return `${n} × ${label}`;
  return `${n} ${label}`;
}

/**
 * Format a base-unit quantity of an ingredient for the kitchen.
 *
 * @param baseQty quantity in the ingredient's base unit (g, ml or each)
 * @param entered for style 'entered': the line as entered; without it 'entered' shows metric
 */
export function formatMeasure(
  baseQty: number,
  ingredient: ConvertibleIngredient,
  style: MeasureStyle,
  entered?: EnteredMeasure | null,
): string {
  if (!Number.isFinite(baseQty)) return '';
  switch (style) {
    case 'entered': {
      const text = entered ? formatEntered(entered) : null;
      return text ?? formatMeasure(baseQty, ingredient, 'metric');
    }
    case 'metric':
      if (ingredient.base_unit === 'g') return formatMetric(baseQty, 'g', 'kg');
      if (ingredient.base_unit === 'ml') return formatMetric(baseQty, 'ml', 'l');
      return formatCount(baseQty);
    case 'us_weight':
      return formatUsWeight(baseQty, ingredient);
    case 'us_volume': {
      const ml = millilitresOf(baseQty, ingredient);
      return ml === null ? formatUsWeight(baseQty, ingredient) : formatKitchenVolume(ml);
    }
    case 'imperial': {
      if (ingredient.base_unit === 'ml') return formatImperialVolume(baseQty);
      const grams = gramsOf(baseQty, ingredient);
      return grams === null ? formatCount(baseQty) : formatImperialMass(grams);
    }
  }
}
